if (typeof InteractiveGraphic !== 'function') {
  class InteractiveGraphic extends HTMLElement {
    constructor() {
      super();

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onBlockSelect = this._onBlockSelect.bind(this);
      this._applyScrollPosition = this._applyScrollPosition.bind(this);

      this._scrollRafId = null;
      this._smoothCarton = { narrow: 0, crossfade: 0, mid: 0 };

      this._scrollTrack = this.querySelector('.interactive-graphic__scroll-track');
      this._steps = [...this.querySelectorAll('.interactive-graphic__step')];
      this._overlays = [...this.querySelectorAll('.interactive-graphic__overlay')];
      this._visual = this.querySelector('.interactive-graphic__visual');
      this._cartonFrame = this.querySelector('.interactive-graphic__carton-frame');
      this._cartonMid = this.querySelector('.interactive-graphic__carton-mid');
      this._cartonFinal = this.querySelector('.interactive-graphic__carton-final');
      this._background = this.querySelector('.interactive-graphic__background');
      this._stepCount = this._steps.length || 1;
      this._activeIndex = -1;
      this._isScrollEnabled = false;
      this._isMobile = false;
      this._contentResizeObserver = null;
      this._stickyHeaderObserver = null;
      this._onStickyHeaderResize = null;
      this._cartonFrameIndex = this._cartonFrame
        ? parseInt(this._cartonFrame.dataset.stepIndex, 10)
        : -1;
      // Equals _cartonFrameIndex when the rise and the final crossfade are one
      // merged step, or the following step when they are authored separately.
      this._cartonFinalIndex = this._cartonFinal
        ? parseInt(this._cartonFinal.dataset.stepIndex, 10)
        : this._cartonFrameIndex + 1;
      this._cartonNarrowEndScale = null;
      this._scrollExtent = 1;
    }

    connectedCallback() {
      this._setupMode();
      this._computeCartonNarrowEndScale();
      this._computeScrollExtent();
      this._setupMobileLayoutObserver();
      this._setupStickyHeaderOffset();

      if (this._isScrollEnabled) {
        this.classList.add('interactive-graphic--scroll-rise');
        window.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onResize, { passive: true });
        requestAnimationFrame(() => {
          this._measureMobileLayout();
          this._measureCopyExtents();
          this._applyScrollPosition();
        });
      } else {
        this.classList.remove('interactive-graphic--scroll-rise');
        this.setActiveStep(0);
      }

      if (Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this._onBlockSelect);
        document.addEventListener('shopify:section:load', () => {
          this._setupMode();
          this._computeScrollExtent();
        });
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      this._contentResizeObserver?.disconnect();
      this._teardownStickyHeaderOffset();
      if (this._scrollRafId) {
        cancelAnimationFrame(this._scrollRafId);
        this._scrollRafId = null;
      }
    }

    _setupMobileLayoutObserver() {
      this._contentResizeObserver?.disconnect();

      if (!this._isMobile) return;

      const content = this.querySelector('.interactive-graphic__content');
      if (!content || typeof ResizeObserver === 'undefined') return;

      this._contentResizeObserver = new ResizeObserver(() => this._measureMobileLayout());
      this._contentResizeObserver.observe(content);
    }

    _applyStickyHeaderOffset() {
      const stickyHeader = document.querySelector('.sticky-header');
      const offset = stickyHeader?.classList.contains('show') ? stickyHeader.offsetHeight : 0;

      document.documentElement.style.setProperty(
        '--interactive-graphic-sticky-header-offset',
        `${offset}px`
      );
      this._measureMobileLayout();
    }

    _setupStickyHeaderOffset() {
      this._teardownStickyHeaderOffset();

      if (!this._isMobile) {
        document.documentElement.style.removeProperty('--interactive-graphic-sticky-header-offset');
        return;
      }

      const stickyHeader = document.querySelector('.sticky-header');
      if (!stickyHeader) return;

      this._applyStickyHeaderOffset();

      this._onStickyHeaderResize = () => this._applyStickyHeaderOffset();
      window.addEventListener('resize', this._onStickyHeaderResize, { passive: true });

      if (typeof MutationObserver !== 'undefined') {
        this._stickyHeaderObserver = new MutationObserver(() => this._applyStickyHeaderOffset());
        this._stickyHeaderObserver.observe(stickyHeader, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }
    }

    _teardownStickyHeaderOffset() {
      this._stickyHeaderObserver?.disconnect();
      this._stickyHeaderObserver = null;

      if (this._onStickyHeaderResize) {
        window.removeEventListener('resize', this._onStickyHeaderResize);
        this._onStickyHeaderResize = null;
      }
    }

    _measureMobileLayout() {
      if (!this._isMobile) {
        this.style.removeProperty('--interactive-graphic-mobile-content-height');
        return;
      }

      const content = this.querySelector('.interactive-graphic__content');
      const container = this.querySelector('.interactive-graphic__container');

      if (!content || !container) return;

      const containerStyles = getComputedStyle(container);
      const gap = parseFloat(containerStyles.rowGap || containerStyles.gap) || 0;
      const paddingTop = parseFloat(containerStyles.paddingTop) || 0;
      const paddingBottom = parseFloat(containerStyles.paddingBottom) || 0;

      this.style.setProperty(
        '--interactive-graphic-mobile-content-height',
        `${content.offsetHeight + gap + paddingTop + paddingBottom}px`
      );
    }

    _setupMode() {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      const isDesignMode = !!Shopify?.designMode;

      this.classList.toggle('interactive-graphic--design-mode', isDesignMode);
      this.classList.toggle('interactive-graphic--reduced-motion', prefersReducedMotion);

      this._isMobile = !isDesignMode && !prefersReducedMotion && isMobile;
      this._isScrollEnabled = !isDesignMode && !prefersReducedMotion && this._stepCount > 0;
      this.classList.toggle('interactive-graphic--mobile', this._isMobile);
      this.classList.toggle('interactive-graphic--scroll-rise', this._isScrollEnabled);

      if (this._isMobile) {
        this._measureMobileLayout();
      } else {
        this.style.removeProperty('--interactive-graphic-mobile-content-height');
      }
    }

    _onResize() {
      const wasEnabled = this._isScrollEnabled;
      const wasMobile = this._isMobile;
      this._setupMode();
      this._computeCartonNarrowEndScale();
      this._computeScrollExtent();
      this._measureCopyExtents();

      if (this._isMobile !== wasMobile) {
        this._setupMobileLayoutObserver();
        this._setupStickyHeaderOffset();
      }

      if (this._isMobile) {
        this._measureMobileLayout();
      }

      if (this._isScrollEnabled && !wasEnabled) {
        window.addEventListener('scroll', this._onScroll, { passive: true });
        this._applyScrollPosition();
      } else if (!this._isScrollEnabled && wasEnabled) {
        window.removeEventListener('scroll', this._onScroll);
        if (this._scrollRafId) {
          cancelAnimationFrame(this._scrollRafId);
          this._scrollRafId = null;
        }
        this.setActiveStep(0);
      } else if (this._isScrollEnabled) {
        this._applyScrollPosition();
      }
    }

    _onScroll() {
      if (!this._scrollTrack || !this._isScrollEnabled) return;
      this._scheduleScrollUpdate();
    }

    _scheduleScrollUpdate() {
      if (this._scrollRafId) return;

      this._scrollRafId = requestAnimationFrame(() => {
        this._scrollRafId = null;
        const stillSmoothing = this._applyScrollPosition();
        if (stillSmoothing) {
          this._scheduleScrollUpdate();
        }
      });
    }

    _applyScrollPosition() {
      if (!this._scrollTrack || !this._isScrollEnabled) return false;

      const rect = this._scrollTrack.getBoundingClientRect();
      const scrollableDistance = rect.height - window.innerHeight;

      if (scrollableDistance <= 0) {
        this._updateScrollState(0, 0, true);
        return false;
      }

      const progress = Math.min(
        this._scrollExtent || 1,
        Math.max(0, (-rect.top / scrollableDistance) * (this._scrollExtent || 1))
      );
      const stepIndex = Math.min(
        this._stepCount - 1,
        Math.floor(Math.min(1, Math.max(0, -rect.top / scrollableDistance)) * this._stepCount)
      );

      return this._updateScrollState(stepIndex, progress);
    }

    _getCartonSmoothFactor() {
      const styles = getComputedStyle(this);
      const factor = parseFloat(styles.getPropertyValue('--interactive-graphic-carton-smooth')) || 0.22;

      return Math.min(1, Math.max(0.08, factor));
    }

    _lerp(current, target, factor) {
      if (Math.abs(target - current) < 0.0008) return target;

      return current + (target - current) * factor;
    }

    _onBlockSelect(event) {
      if (!this.contains(event.target)) return;

      const step = event.target.closest('[data-step-index]');
      if (!step) return;

      this.setActiveStep(parseInt(step.dataset.stepIndex, 10));
    }

    _getTimingVars() {
      const styles = getComputedStyle(this);

      return {
        segmentSize: 1 / this._stepCount,
        lead: this._readTiming(styles, 'rise-lead', 1),
        enter: this._readTiming(styles, 'enter', 1),
        hold: this._readTiming(styles, 'hold', 0),
        exit: this._readTiming(styles, 'exit', 0.8),
        stackDelay: this._readTiming(styles, 'stack-delay', 0),
        cartonRise: this._readTiming(styles, 'carton-rise', 1),
        cartonRiseLead: this._readTiming(styles, 'carton-rise-lead', 1),
        cartonRiseHold: this._readTiming(styles, 'carton-rise-hold', 0),
        cartonMidFade: this._readTiming(styles, 'carton-mid-fade', 0.38),
        cartonNarrow: this._readTiming(styles, 'carton-narrow', 0.9),
        cartonCrossfade: this._readTiming(styles, 'carton-crossfade', 0.3),
        textCue: Math.min(1, Math.max(0, this._readTiming(styles, 'text-cue', 0.5))),
        textEnter: Math.min(1, Math.max(0, this._readTiming(styles, 'text-enter', 0.4))),
        textExit: Math.min(1, Math.max(0, this._readTiming(styles, 'text-exit', 0.4))),
        textApproach: Math.max(0.05, this._readTiming(styles, 'text-approach', 0.7)),
        textTravel: Math.max(0, this._readTiming(styles, 'text-travel', 0.75)),
        textFadeBias: Math.min(0.9, Math.max(0, this._readTiming(styles, 'text-fade-bias', 0.35))),
      };
    }

    /**
     * Timing vars are fractions of a step segment and 0 is a meaningful value
     * (no hold), so an explicit NaN check is required — `|| fallback` would
     * quietly restore a pause.
     */
    _readTiming(styles, name, fallback) {
      const value = parseFloat(styles.getPropertyValue(`--interactive-graphic-${name}`));

      return Number.isNaN(value) ? fallback : value;
    }

    _easeInOutSine(value) {
      if (value <= 0) return 0;
      if (value >= 1) return 1;

      return -(Math.cos(Math.PI * value) - 1) / 2;
    }

    _phaseProgress(progress, start, end) {
      if (progress <= start) return 0;
      if (progress >= end) return 1;

      return this._easeInOutSine((progress - start) / (end - start));
    }

    _overlayExitsAtIndex(index) {
      const overlay = this._overlays.find((el) => parseInt(el.dataset.stepIndex, 10) === index);

      return overlay ? this._exitsOnNextStep(overlay) : true;
    }

    /**
     * Explicit enter → hold → exit window per overlay (scroll progress 0–1).
     * Phases are anchored to each overlay's step segment.
     */
    _getOverlayTimeline(overlayIndex, exitsOnNext) {
      const { segmentSize, lead, enter, hold, exit, stackDelay } = this._getTimingVars();
      const seg = segmentSize;
      const stepStart = overlayIndex * seg;
      const stepEnd = Math.min(1, (overlayIndex + 1) * seg);

      let enterStart = stepStart - lead * seg;

      if (overlayIndex > 0) {
        const prevTimeline = this._getOverlayTimeline(
          overlayIndex - 1,
          this._overlayExitsAtIndex(overlayIndex - 1)
        );
        enterStart = Math.max(enterStart, prevTimeline.holdEnd);
      }

      let enterEnd = enterStart + enter * seg;
      let holdEnd = enterEnd + hold * seg;
      let exitEnd = exitsOnNext ? holdEnd + exit * seg : holdEnd;

      // Persistent step before carton: extended hold (e.g. golden rice)
      const preCartonIndex = this._cartonFrameIndex - 1;
      if (
        !exitsOnNext
        && preCartonIndex >= 0
        && overlayIndex === preCartonIndex
        && !this._overlayExitsAtIndex(preCartonIndex)
      ) {
        holdEnd = enterEnd + (hold + stackDelay) * seg;
        exitEnd = holdEnd;
      }

      // Carton frame rise is handled by _getCartonSequence — keep timeline for enter anchor only
      if (!exitsOnNext && overlayIndex === this._cartonFrameIndex) {
        enterStart = stepStart - lead * seg;
        if (overlayIndex > 0) {
          const prevTimeline = this._getOverlayTimeline(
            overlayIndex - 1,
            this._overlayExitsAtIndex(overlayIndex - 1)
          );
          enterStart = Math.max(enterStart, prevTimeline.holdEnd);
        }
        enterEnd = enterStart + enter * seg;
        holdEnd = stepEnd;
        exitEnd = stepEnd;
      }

      enterStart = Math.max(0, enterStart);
      enterEnd = Math.min(Math.max(enterEnd, enterStart + seg * 0.08), 1);
      holdEnd = Math.max(holdEnd, enterEnd);
      exitEnd = Math.max(exitEnd, holdEnd);

      return { enterStart, enterEnd, holdEnd, exitEnd, stepStart, stepEnd };
    }

    /**
     * Scroll progress bounds for the carton frame → final sequence.
     */
    _getCartonTimings() {
      if (this._cartonFrameIndex < 0) return null;

      const {
        segmentSize,
        cartonRise,
        cartonRiseLead,
        cartonRiseHold,
        cartonMidFade,
        cartonNarrow,
        cartonCrossfade,
      } = this._getTimingVars();
      const seg = segmentSize;
      const stepStart = this._cartonFrameIndex * seg;

      let riseStart = Math.max(0, stepStart - cartonRiseLead * seg);
      const preCartonIndex = this._cartonFrameIndex - 1;

      if (preCartonIndex >= 0 && !this._overlayExitsAtIndex(preCartonIndex)) {
        const preCartonTimeline = this._getOverlayTimeline(preCartonIndex, false);
        riseStart = Math.max(riseStart, preCartonTimeline.holdEnd);
      }

      const riseEnd = riseStart + cartonRise * seg;
      // Bridge crossfade sits at the apex of the rise. Zero-width when no bridge
      // image is configured, so the original timeline is preserved exactly.
      const midStart = riseEnd;
      const midEnd = midStart + (this._cartonMid ? cartonMidFade * seg : 0);
      const narrowStart = midEnd + cartonRiseHold * seg;
      const narrowEnd = narrowStart + cartonNarrow * seg;
      const crossfadeStart = narrowEnd;
      const crossfadeEnd = crossfadeStart + cartonCrossfade * seg;

      return {
        riseStart,
        riseEnd,
        midStart,
        midEnd,
        narrowStart,
        narrowEnd,
        crossfadeStart,
        crossfadeEnd,
      };
    }

    /**
     * Step 5→6 carton sequence within scroll progress 0–1:
     * 1. Rise transparent frame to top
     * 2. Hold at top
     * 3. Narrow composite until full carton fits
     * 4. Crossfade to final full carton image
     */
    _getCartonSequence(progress) {
      if (this._cartonFrameIndex < 0) {
        return { riseAmount: 0, midAmount: 0, narrowAmount: 0, crossfadeAmount: 0 };
      }

      const timings = this._getCartonTimings();
      const {
        riseStart,
        riseEnd,
        midStart,
        midEnd,
        narrowStart,
        narrowEnd,
        crossfadeStart,
        crossfadeEnd,
      } = timings;

      const riseAmount = this._phaseProgress(progress, riseStart, riseEnd);
      const midAmount = this._cartonMid
        ? this._phaseProgress(progress, midStart, midEnd)
        : 0;
      const narrowAmount = progress >= narrowStart
        ? this._phaseProgress(progress, narrowStart, narrowEnd)
        : 0;
      const crossfadeAmount = narrowAmount >= 1
        ? this._phaseProgress(progress, crossfadeStart, crossfadeEnd)
        : 0;

      return {
        riseAmount,
        midAmount,
        narrowAmount,
        crossfadeAmount,
        stepStart: this._cartonFrameIndex / this._stepCount,
        stepEnd: (this._cartonFrameIndex + 1) / this._stepCount,
      };
    }

    _getEnterAmount(progress, overlayIndex, exitsOnNext) {
      const timeline = this._getOverlayTimeline(overlayIndex, exitsOnNext);

      return this._phaseProgress(progress, timeline.enterStart, timeline.enterEnd);
    }

    _getOverlayMotion(overlay, stepIndex, progress) {
      const overlayIndex = parseInt(overlay.dataset.stepIndex, 10);
      const exitsOnNext = this._exitsOnNextStep(overlay);
      const timeline = this._getOverlayTimeline(overlayIndex, exitsOnNext);

      let amount = this._getEnterAmount(progress, overlayIndex, exitsOnNext);

      if (progress >= timeline.enterEnd && progress < timeline.holdEnd) {
        amount = 1;
      }

      // Crossfade: current overlay sinks only after its hold, as the next one rises
      if (exitsOnNext && overlayIndex < this._stepCount - 1) {
        const nextIndex = overlayIndex + 1;

        if (nextIndex !== this._cartonFrameIndex && progress >= timeline.holdEnd) {
          const nextTimeline = this._getOverlayTimeline(
            nextIndex,
            this._overlayExitsAtIndex(nextIndex)
          );
          const nextEnter = this._phaseProgress(
            progress,
            nextTimeline.enterStart,
            nextTimeline.enterEnd
          );

          if (nextEnter > 0) {
            amount = Math.min(amount, 1 - nextEnter);
          }
        }
      }

      // Persistent overlays stay up after entering
      if (!exitsOnNext && overlayIndex !== this._cartonFrameIndex && progress >= timeline.enterEnd) {
        amount = 1;
      }

      return {
        amount,
        visible: amount > 0.01,
        y: this._getOverlayY(overlay, amount),
      };
    }

    _exitsOnNextStep(overlay) {
      return overlay.dataset.exitOnNextStep !== 'false';
    }

    _getOverlayHiddenY(overlay) {
      if (overlay.dataset.overlaySlot === 'underlay') return 50;
      return 55;
    }

    _getOverlayRestY(overlay) {
      const slot = overlay.dataset.overlaySlot;

      if (slot === 'carton-frame' || slot === 'carton-final') {
        const restValue = getComputedStyle(overlay).getPropertyValue('--overlay-rise-rest').trim();
        return restValue ? parseFloat(restValue) : 0;
      }

      if (slot === 'underlay') {
        const restValue = getComputedStyle(overlay).getPropertyValue('--overlay-rise-rest-underlay').trim();
        return restValue ? parseFloat(restValue) : -10;
      }

      const restValue = getComputedStyle(overlay).getPropertyValue('--overlay-rise-rest').trim();
      return restValue ? parseFloat(restValue) : -18;
    }

    _getOverlayY(overlay, amount) {
      const hiddenY = this._getOverlayHiddenY(overlay);
      const restY = this._getOverlayRestY(overlay);

      return hiddenY + (restY - hiddenY) * amount;
    }

    _getOverlayEnterAmount(progress, overlayIndex) {
      return this._getEnterAmount(progress, overlayIndex, true);
    }

    /**
     * Uniform end scale so the full carton PNG (including bottom) fits the landscape stage.
     * height at start = (cartonWidth% / 100) * stageWidth / aspectRatio
     */
    _computeCartonNarrowEndScale() {
      if (!this._visual || !this._cartonFrame) return;

      const img = this._cartonFrame.querySelector('img');

      if (!img) return;

      const applyScale = () => {
        const visualRect = this._visual.getBoundingClientRect();
        if (!visualRect.width || !visualRect.height) return;

        const styles = getComputedStyle(this);
        const widthRatio = parseFloat(styles.getPropertyValue('--interactive-graphic-carton-width-ratio')) || 1.75;
        const trimTop = parseFloat(styles.getPropertyValue('--interactive-graphic-carton-trim-top')) || 67;
        const trimRef = parseFloat(styles.getPropertyValue('--interactive-graphic-carton-trim-ref-width')) || 840;
        const fallbackAspect = parseFloat(styles.getPropertyValue('--interactive-graphic-carton-aspect')) || 0.762195;
        const aspect = img.naturalWidth && img.naturalHeight
          ? img.naturalWidth / img.naturalHeight
          : fallbackAspect;

        const renderedWidth = visualRect.width * widthRatio;
        const topTrim = (trimTop / trimRef) * renderedWidth;
        const renderedHeight = renderedWidth / aspect;
        const availableHeight = visualRect.height + topTrim;
        const scaleToFitHeight = availableHeight / renderedHeight;
        const scaleToFitWidth = visualRect.width / renderedWidth;
        const endScale = Math.min(scaleToFitHeight, scaleToFitWidth, 1);

        this._visual.style.setProperty('--interactive-graphic-carton-narrow-end-scale', endScale);
        this._cartonNarrowEndScale = endScale;
      };

      if (img.complete && img.naturalWidth) {
        applyScale();
      } else {
        img.addEventListener('load', applyScale, { once: true });
      }
    }

    _getCartonCrossfadeEnd() {
      const timings = this._getCartonTimings();

      return timings ? timings.crossfadeEnd : 1;
    }

    _computeScrollExtent() {
      const styles = getComputedStyle(this);
      const finalHold = this._readTiming(styles, 'final-hold', 0.02);
      const cartonEnd = this._getCartonCrossfadeEnd();

      this._scrollExtent = Math.max(1, cartonEnd + finalHold);
      this.style.setProperty('--interactive-graphic-scroll-extent', this._scrollExtent);
    }

    /**
     * Scroll progress over which a step's graphic travels into frame.
     * Steps with no artwork of their own collapse to a point at their segment start.
     */
    _getGraphicEntryWindow(stepIndex) {
      const seg = 1 / this._stepCount;
      const point = { start: stepIndex * seg, end: stepIndex * seg };

      if (this._cartonFrameIndex >= 0) {
        const timings = this._getCartonTimings();

        if (!timings) return point;

        // A merged carton step owns the whole sequence, so its entry is the rise.
        if (stepIndex === this._cartonFinalIndex && this._cartonFinalIndex !== this._cartonFrameIndex) {
          return { start: timings.crossfadeStart, end: timings.crossfadeEnd };
        }

        if (stepIndex === this._cartonFrameIndex) {
          return { start: timings.riseStart, end: timings.riseEnd };
        }
      }

      if (this._overlays.some((overlay) => parseInt(overlay.dataset.stepIndex, 10) === stepIndex)) {
        const timeline = this._getOverlayTimeline(
          stepIndex,
          this._overlayExitsAtIndex(stepIndex)
        );

        return { start: timeline.enterStart, end: timeline.enterEnd };
      }

      return point;
    }

    /**
     * Scroll progress when a step's graphic reaches its peak (fully risen / visible).
     */
    _getGraphicApogeeProgress(stepIndex) {
      return this._getGraphicEntryWindow(stepIndex).end;
    }

    /**
     * Scroll progress when a step's copy takes over. Anchored part-way through the
     * travel of that step's graphic (--interactive-graphic-text-cue) rather than at
     * the end of it, so the copy changes with the image that is moving into frame
     * instead of a full step behind it.
     */
    _getTextCueProgress(stepIndex) {
      const { textCue } = this._getTimingVars();
      const { start, end } = this._getGraphicEntryWindow(stepIndex);

      return start + (end - start) * textCue;
    }

    /**
     * Scroll progress when a step's copy is fully in place and readable.
     * The opening copy lands at 0 — it rides the section's approach rather than
     * any of the pinned scroll, so it has finished arriving by the time the
     * section pins. See _getApproachAmount.
     */
    _getTextLandProgress(stepIndex) {
      if (stepIndex === 0) return 0;

      return this._getTextCueProgress(stepIndex);
    }

    /**
     * Each step's copy rides the scroll the way its overlay does: up into place,
     * a readable hold at the apex, then up and out as the next step arrives.
     * Enter and exit are fractions of the gap to the neighbouring step, so the
     * hold is whatever is left in the middle.
     */
    _getTextMotion(stepIndex, progress) {
      const { textEnter, textExit } = this._getTimingVars();
      const land = this._getTextLandProgress(stepIndex);
      const prevLand = stepIndex > 0 ? this._getTextLandProgress(stepIndex - 1) : 0;
      // The opening copy is already landed for the whole pinned sequence; its rise
      // happened on the way in, and _updateTextMotion scales it by the approach.
      const enterAmount = stepIndex === 0
        ? 1
        : this._phaseProgress(progress, land - textEnter * (land - prevLand), land);

      // The closing step has no next step to hand off to, so its copy travels on
      // over whatever scroll is left rather than parking in the middle of the frame
      // while the carton finishes — a hold there is the one pause the reader would
      // still hit.
      const nextLand = stepIndex < this._stepCount - 1
        ? this._getTextLandProgress(stepIndex + 1)
        : (this._scrollExtent || 1);
      const exitStart = nextLand - textExit * (nextLand - land);
      const exitAmount = nextLand > land
        ? this._phaseProgress(progress, exitStart, nextLand)
        : 0;

      // +1 waiting below the frame, 0 centred, -1 gone above it.
      return 1 - enterAmount - exitAmount;
    }

    /**
     * Copy-block height per step, and the lane they travel through. Needed to fade
     * each block on its way out of frame rather than on its way through the middle.
     */
    _measureCopyExtents() {
      const lane = this.querySelector('.interactive-graphic__steps');

      this._laneHeight = lane ? lane.offsetHeight : 0;
      this._copyHeights = this._steps.map((step) => {
        const parts = [...step.children];

        if (!parts.length) return 0;

        const first = parts[0];
        const last = parts[parts.length - 1];

        return last.offsetTop + last.offsetHeight - first.offsetTop;
      });
    }

    /**
     * A centred block is still wholly inside the frame until it has travelled half
     * the lane less half its own height, and is wholly clear of it half a block
     * later. Fading over exactly that span keeps the copy solid and readable the
     * whole way through the middle — with no hold, an opacity tied to the travel
     * itself would leave it half-transparent almost everywhere — and lets the fade
     * follow it out of frame.
     */
    _getTextOpacity(stepIndex, offset) {
      const travel = this._getTimingVars().textTravel * (this._laneHeight || 0);
      const copyHeight = this._copyHeights?.[stepIndex] ?? 0;

      if (travel <= 0 || !copyHeight) return Math.abs(offset) >= 1 ? 0 : 1;

      const { textFadeBias } = this._getTimingVars();
      const touching = Math.max(0, (this._laneHeight - copyHeight) / 2 / travel);
      const fadeEnd = (this._laneHeight + copyHeight) / 2 / travel;
      // Holding full opacity a little past the edge lets the copy slide off the way
      // ordinary page copy does, instead of dimming the moment it starts to clip —
      // which matters most where the lane is barely taller than the copy.
      const fadeStart = touching + textFadeBias * (fadeEnd - touching);
      const distance = Math.abs(offset);

      if (distance <= fadeStart) return 1;
      if (distance >= fadeEnd) return 0;

      return 1 - this._easeInOutSine((distance - fadeStart) / (fadeEnd - fadeStart));
    }

    /**
     * How far the opening copy has travelled in, 0–1. It starts rising while the
     * section is still approaching — so the visitor watches it scroll into place
     * rather than finding it already sitting there — and finishes just as the
     * first overlay starts to rise, handing the motion over to the scene.
     */
    _getApproachAmount() {
      if (!this._scrollTrack) return 1;

      const { textApproach } = this._getTimingVars();
      const rect = this._scrollTrack.getBoundingClientRect();
      const scrollableDistance = rect.height - window.innerHeight;
      const start = window.innerHeight * textApproach;
      // Track offset (negative, i.e. past the pin) at which the copy has landed.
      let end = 0;

      if (this._stepCount > 1 && scrollableDistance > 0) {
        const firstMove = this._getGraphicEntryWindow(1).start;

        end = -(firstMove / (this._scrollExtent || 1)) * scrollableDistance;
      }

      if (start <= end) return 1;

      return Math.min(1, Math.max(0, (start - rect.top) / (start - end)));
    }

    _updateTextMotion(progress) {
      const approach = this._easeInOutSine(this._getApproachAmount());

      if (!this._copyHeights) this._measureCopyExtents();

      this._steps.forEach((step, i) => {
        const offset = this._getTextMotion(i, progress);
        // The opening copy rises over the section's approach, so it is held below
        // the frame until the section is on its way in.
        const stepOffset = i === 0 && approach < 1 ? offset + (1 - approach) : offset;
        const opacity = this._getTextOpacity(i, stepOffset);

        step.style.setProperty('--step-progress', opacity);
        step.style.setProperty('--step-offset', stepOffset);
        step.classList.toggle('is-visible', opacity > 0.001);
      });
    }

    _getTextStepIndex(_stepIndex, progress) {
      let textStep = 0;

      for (let i = 0; i < this._stepCount; i++) {
        if (progress >= this._getTextLandProgress(i)) {
          textStep = i;
        }
      }

      return textStep;
    }

    _getHoldProgress(stepIndex) {
      const apogee = this._getGraphicApogeeProgress(stepIndex);
      const seg = 1 / this._stepCount;

      return Math.min(this._scrollExtent || 1, apogee + seg * 0.08);
    }

    _updateCartonSequence(progress, snap = false) {
      if (!this._visual || this._cartonFrameIndex < 0) return false;

      const { riseAmount, midAmount, narrowAmount, crossfadeAmount } = this._getCartonSequence(progress);
      const factor = snap ? 1 : this._getCartonSmoothFactor();
      const smoothMid = snap
        ? midAmount
        : this._lerp(this._smoothCarton.mid, midAmount, factor);
      const smoothNarrow = snap
        ? narrowAmount
        : this._lerp(this._smoothCarton.narrow, narrowAmount, factor);
      const smoothCrossfade = snap
        ? crossfadeAmount
        : this._lerp(this._smoothCarton.crossfade, crossfadeAmount, factor);

      this._smoothCarton.mid = smoothMid;
      this._smoothCarton.narrow = smoothNarrow;
      this._smoothCarton.crossfade = smoothCrossfade;

      this._visual.style.setProperty('--carton-narrow-amount', smoothNarrow);
      this._visual.style.setProperty('--carton-crossfade-amount', smoothCrossfade);
      this._visual.style.setProperty('--carton-mid-amount', smoothMid);

      if (this._cartonFrame) {
        const y = this._getOverlayY(this._cartonFrame, riseAmount);
        // Frame hands off to the bridge image at its apex, then to the final.
        const frameOpacity = riseAmount * (1 - smoothMid) * (1 - smoothCrossfade);

        this._cartonFrame.style.setProperty('--overlay-rise-progress', frameOpacity);
        this._cartonFrame.style.setProperty('--overlay-rise-y', `${y}%`);
        this._cartonFrame.classList.toggle(
          'is-visible',
          riseAmount > 0.01 && smoothMid < 0.99 && smoothCrossfade < 0.99
        );
      }

      if (this._cartonMid) {
        const midOpacity = smoothMid * (1 - smoothCrossfade);

        this._cartonMid.style.setProperty('--carton-mid-opacity', midOpacity);
        this._cartonMid.classList.toggle('is-visible', midOpacity > 0.01);
      }

      if (this._cartonFinal) {
        this._cartonFinal.style.setProperty('--carton-final-opacity', smoothCrossfade);
        this._cartonFinal.classList.toggle('is-visible', smoothCrossfade > 0.01);
      }

      const cartonActive = riseAmount > 0.05 || smoothMid > 0 || smoothNarrow > 0 || smoothCrossfade > 0;
      this.classList.toggle('interactive-graphic--carton-active', cartonActive);
      this.classList.toggle('interactive-graphic--carton-narrowing', smoothNarrow > 0 && smoothNarrow < 1);
      this.classList.toggle('interactive-graphic--carton-final', smoothCrossfade > 0.08);

      if (snap) return false;

      return (
        Math.abs(smoothMid - midAmount) > 0.001
        || Math.abs(smoothNarrow - narrowAmount) > 0.001
        || Math.abs(smoothCrossfade - crossfadeAmount) > 0.001
      );
    }

    _updateOverlayMotion(stepIndex, progress) {
      this._overlays.forEach((overlay) => {
        let motion;

        if (!this._isScrollEnabled) {
          const overlayIndex = parseInt(overlay.dataset.stepIndex, 10);
          const exitsOnNext = this._exitsOnNextStep(overlay);
          const visible = exitsOnNext
            ? stepIndex === overlayIndex
            : stepIndex >= overlayIndex;
          const amount = visible ? 1 : 0;

          motion = {
            amount,
            visible,
            y: this._getOverlayY(overlay, amount),
          };
        } else {
          motion = this._getOverlayMotion(overlay, stepIndex, progress);
        }

        let amount = motion.amount;

        // The persistent overlay before the carton (golden rice) fades out with
        // the frame as the bridge image takes over at the rise apex.
        if (this._cartonMid && this._cartonFrameIndex > 0) {
          const overlayIndex = parseInt(overlay.dataset.stepIndex, 10);

          if (overlayIndex === this._cartonFrameIndex - 1) {
            amount *= 1 - this._smoothCarton.mid;
          }
        }

        overlay.style.setProperty('--overlay-rise-progress', amount);
        overlay.style.setProperty('--overlay-rise-y', `${motion.y}%`);
        overlay.classList.toggle('is-visible', motion.visible && amount > 0.01);
      });
    }

    _updateCartonSequenceStatic(stepIndex) {
      if (!this._visual || this._cartonFrameIndex < 0) return;

      const isMergedCarton = this._cartonFinalIndex === this._cartonFrameIndex;
      // With one merged step the full carton is that step's end state, so the still
      // shows it rather than the transparent frame it passes through on the way.
      const onCartonStep = stepIndex === this._cartonFrameIndex && !isMergedCarton;
      const onFinalStep = stepIndex === this._cartonFinalIndex;

      // Only the finished carton sits at the narrowed scale; every earlier step
      // shows the scene at full size.
      this._smoothCarton.narrow = onFinalStep ? 1 : 0;
      this._smoothCarton.crossfade = onFinalStep ? 1 : 0;
      // No sub-step animation in the static fallback: the bridge image stands in
      // for the frame on the carton step so the still matches the animated end state.
      this._smoothCarton.mid = this._cartonMid && onCartonStep ? 1 : 0;

      this._visual.style.setProperty('--carton-narrow-amount', this._smoothCarton.narrow);
      this._visual.style.setProperty('--carton-crossfade-amount', onFinalStep ? 1 : 0);
      this._visual.style.setProperty('--carton-mid-amount', this._smoothCarton.mid);

      if (this._cartonFrame) {
        const amount = onCartonStep ? 1 : 0;
        const showFrame = onCartonStep && !this._cartonMid;
        this._cartonFrame.style.setProperty('--overlay-rise-progress', showFrame ? 1 : 0);
        this._cartonFrame.style.setProperty('--overlay-rise-y', `${this._getOverlayY(this._cartonFrame, amount)}%`);
        this._cartonFrame.classList.toggle('is-visible', showFrame);
      }

      if (this._cartonMid) {
        this._cartonMid.style.setProperty('--carton-mid-opacity', this._smoothCarton.mid);
        this._cartonMid.classList.toggle('is-visible', this._smoothCarton.mid > 0);
      }

      if (this._cartonFinal) {
        this._cartonFinal.style.setProperty('--carton-final-opacity', onFinalStep ? 1 : 0);
        this._cartonFinal.classList.toggle('is-visible', onFinalStep);
      }

      this.classList.toggle('interactive-graphic--carton-active', onCartonStep || onFinalStep);
      this.classList.toggle('interactive-graphic--carton-final', onFinalStep);
    }

    _updateBackgroundReveal(stepIndex, progress) {
      if (!this._background) return;

      const fromStep = parseInt(this._background.dataset.visibleFromStep, 10);

      if (fromStep <= 0) {
        this._background.classList.add('is-visible');
        return;
      }

      const shouldShow = this._isScrollEnabled
        ? stepIndex >= fromStep || this._getOverlayEnterAmount(progress, fromStep) > 0
        : stepIndex >= fromStep;

      this._background.classList.toggle('is-visible', shouldShow);
    }

    _updateScrollState(stepIndex, progress, snap = false) {
      const textStepIndex = this._getTextStepIndex(stepIndex, progress);

      if (textStepIndex !== this._activeIndex) {
        this._activeIndex = textStepIndex;

        this._steps.forEach((step, i) => {
          const isActive = i === textStepIndex;
          step.classList.toggle('is-active', isActive);
          step.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        if (this._isMobile) {
          requestAnimationFrame(() => this._measureMobileLayout());
        }
      }

      this._updateOverlayMotion(stepIndex, progress);
      this._updateBackgroundReveal(stepIndex, progress);

      if (!this._isScrollEnabled) {
        this._updateCartonSequenceStatic(stepIndex);
        return false;
      }

      this._updateTextMotion(progress);

      return this._updateCartonSequence(progress, snap);
    }

    setActiveStep(index) {
      if (this._isScrollEnabled) {
        this._updateScrollState(index, this._getHoldProgress(index), true);
        return;
      }

      if (index === this._activeIndex && !Shopify?.designMode) return;

      this._activeIndex = index;

      this._steps.forEach((step, i) => {
        const isActive = i === index;
        step.classList.toggle('is-active', isActive);
        step.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this._updateOverlayMotion(index, (index + 0.5) / this._stepCount);
      this._updateBackgroundReveal(index, 0);
      this._updateCartonSequenceStatic(index);
    }
  }

  if (typeof customElements.get('interactive-graphic') === 'undefined') {
    customElements.define('interactive-graphic', InteractiveGraphic);
  }
}
